/**
 * Mounts the transaction engine.
 *
 * Responsibilities, all of them invisible to the UI:
 *   - start the journal (cross-tab sync + GC)
 *   - elect a single leader tab so an effect is not fired twice
 *   - classify the connected account as EOA or ERC-4337 smart account
 *   - drive the recovery + outbox loop
 *
 * Renders nothing of its own. If anything in here fails, screens fall back to
 * plain wagmi behaviour — this must never be able to break the app.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAccount, useChainId, usePublicClient } from "wagmi";
import * as journal from "./journal";
import * as leader from "./leader";
import { getStep } from "./flows";
import { processEntry } from "./outbox";
import { resolveEntry } from "./recovery";
import { STATES } from "./machine";
import { TxEngineContext } from "./TxEngineContext";
import { parseUser } from "../utils/storedUser";

const TICK_MS = 4000;

export function TxEngineProvider({ children }) {
  const { address } = useAccount();
  const chainId = useChainId();
  const publicClient = usePublicClient();
  const dispatch = useDispatch();

  const socketInstance = useSelector((state) => state?.UserDetails?.socketInstance);
  const token = useSelector((state) => state?.Authentication?.token);
  // `Authentication.user` holds a JSON STRING, not an object — the reducer
  // normalizes it with `toUserString` and every other consumer calls
  // `parseUser` before destructuring. Handing the raw string to effects made
  // `ctx.user._id` undefined, so `createOrder`'s `verify()` short-circuited to
  // "not yet created" on its first line and the outbox re-sent an order that
  // had already been created. Parse once, here.
  const rawUser = useSelector((state) => state?.Authentication?.user);
  const user = useMemo(() => parseUser(rawUser), [rawUser]);

  const [accountType, setAccountType] = useState("eoa");
  const [isLeader, setIsLeader] = useState(false);

  // --- account classification --------------------------------------------
  // Exact rather than heuristic: a deployed smart account has code at its
  // address. This matters because an ERC-4337 account's EVM nonce does not
  // advance, so the nonce-based recovery path is meaningless for it — see the
  // comment at Collateral.jsx:165 documenting the bug that caused.
  useEffect(() => {
    let cancelled = false;
    if (!address || !publicClient) {
      setAccountType("eoa");
      return undefined;
    }
    (async () => {
      try {
        const code = await publicClient.getCode({ address });
        if (!cancelled) setAccountType(code && code !== "0x" ? "smart" : "eoa");
      } catch (err) {
        // Unknown: assume EOA. Recovery still has the hash and verifier paths.
        if (!cancelled) setAccountType("eoa");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [address, publicClient]);

  // --- journal + leader lifecycle ----------------------------------------
  useEffect(() => {
    journal.start();
    leader.start(setIsLeader);
    return () => {
      leader.stop();
      journal.stop();
    };
  }, []);

  const effectContext = useMemo(
    () => ({ socket: socketInstance, token, user, dispatch, chainId, address }),
    [socketInstance, token, user, dispatch, chainId, address]
  );
  const effectContextRef = useRef(effectContext);
  effectContextRef.current = effectContext;

  // --- the loop -----------------------------------------------------------
  const running = useRef(false);

  const tick = useCallback(async () => {
    if (running.current) return; // never overlap passes
    if (!leader.isLeader()) return;
    if (!address || !publicClient) return;

    running.current = true;
    try {
      const open = journal.openEntriesForScope({ chainId, address });
      for (let i = 0; i < open.length; i += 1) {
        const entry = open[i];
        try {
          if (entry.status === STATES.EFFECTS_PENDING) {
            const action = await processEntry({ entry, ctx: effectContextRef.current });
            if (action) journal.apply(entry.key, action.event, action.payload);
          } else {
            const action = await resolveEntry({
              entry,
              client: publicClient,
              flow: getStep(entry.flowId, entry.stepId),
            });
            if (action) journal.apply(entry.key, action.event, action.payload);
          }
        } catch (err) {
          // One bad row must never stall the rest of the queue.
          console.error("[tx] entry pass failed", entry.key, err);
        }
      }
    } finally {
      running.current = false;
    }
  }, [address, chainId, publicClient]);

  // Journal changes schedule a tick on the next macrotask, so an effect
  // enqueued at confirmation time is delivered immediately rather than waiting
  // out the interval — the user must not see a 4-second stall where today the
  // emit is instant. This converges: a pass that changes nothing emits no
  // notification, so it cannot loop.
  const scheduledRef = useRef(null);
  const scheduleTick = useCallback(() => {
    if (scheduledRef.current) return;
    scheduledRef.current = setTimeout(() => {
      scheduledRef.current = null;
      tick();
    }, 0);
  }, [tick]);

  useEffect(() => {
    const unsubscribe = journal.subscribe(scheduleTick);
    return () => {
      unsubscribe();
      if (scheduledRef.current) clearTimeout(scheduledRef.current);
      scheduledRef.current = null;
    };
  }, [scheduleTick]);

  useEffect(() => {
    if (!address) return undefined;
    tick();
    const id = setInterval(tick, TICK_MS);

    // Coming back to the tab is the single most likely moment for there to be
    // something to recover, so do not wait out the interval.
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [address, tick]);

  const value = useMemo(
    () => ({ accountType, isLeader, ready: !!address, effectContext }),
    [accountType, isLeader, address, effectContext]
  );

  return <TxEngineContext.Provider value={value}>{children}</TxEngineContext.Provider>;
}

export default TxEngineProvider;
